// ═══════════════════════════════════════════════════════════════════════════
// AI → EditorCommand translator — top-level dispatcher (Phase FIN.4)
// ═══════════════════════════════════════════════════════════════════════════
//
// This module exposes the public entry points used by callers:
//   - translateAICommandToDispatch(command, context)
//   - translateAICommandBatch(commands, context)
//
// Implementation is split across specialised translator files to respect
// the CLAUDE.md 500-LOC module limit:
//   - blockDispatcherShared.ts   — AI_COMMAND_SOURCE, context types, helpers
//   - blockDispatcherBasic.ts    — CREATE_BLOCK / INSERT / EXPLODE / UPDATE_ATTRIBUTE
//   - blockDispatcherDynamic.ts  — dynamic blocks + visibility/lookup + cannoscale
//   - blockDispatcherAttribute.ts— ATTDEF CRUD
//   - blockDispatcherLifecycle.ts— PURGE / RENAME
//
// The caller dispatches the returned commands through the SAME pipeline UI
// dispatches through (useEditorStore.dispatch), so validation, audit
// trail, undo history, and shell-resync all work for AI operations without
// any parallel implementation — the enforcement of the CLAUDE.md rule:
//   "AI drafting не имеет права писать сущности напрямую, он может только
//    предлагать и исполнять команды через безопасный контур."

import type { EditorCommand } from "@dxf-ai-editor/contracts";
import {
  ALL_COMMAND_DESCRIPTORS,
  type CommandDescriptor,
} from "@dxf-ai-editor/command-registry";

import type { BlockAiCommand } from "./blockSchemas";
import { type AiDispatchContext } from "./blockDispatcherShared";
import { assertAiCallableAction } from "./aiCallableGuard";
import {
  translateCreateBlock,
  translateExplode,
  translateInsertBlock,
  translateUpdateAttribute,
} from "./blockDispatcherBasic";
import {
  translateCreateDynamicBlock,
  translateLookupSelect,
  translateSetCannoscale,
  translateSetVisibilityState,
  translateUpdateDynamicParameter,
} from "./blockDispatcherDynamic";
import {
  translateCreateAttdef,
  translateRemoveAttdef,
  translateRenameAttdefTag,
  translateReorderAttdefs,
  translateUpdateAttdef,
} from "./blockDispatcherAttribute";
import {
  translatePurgeBlocks,
  translateRenameBlock,
} from "./blockDispatcherLifecycle";

export {
  AI_COMMAND_SOURCE,
  AI_INSERT_GROUP_SIZE_HINT,
  LAST_INSERT_ID_SENTINEL,
  type AiDispatchContext,
} from "./blockDispatcherShared";

export {
  AI_ACTION_TARGET_COMMAND,
  NonAiCallableCommandError,
  assertAiCallableAction,
  assertAiCallableCommand,
  isAiCallableCommand,
} from "./aiCallableGuard";

/**
 * Top-level dispatcher. Takes a validated AI command + optional runtime
 * context (active space, resolvers) and returns the flat sequence of
 * `EditorCommand`s the store should dispatch. The caller is expected to
 * iterate and call `store.dispatch(...)` for each — mirroring the
 * pattern used by `editorStore.executeBatchCommands` for command-line
 * input.
 */
export function translateAICommandToDispatch(
  command: BlockAiCommand,
  context: AiDispatchContext = {},
  descriptors: readonly CommandDescriptor[] = ALL_COMMAND_DESCRIPTORS,
): EditorCommand[] {
  // Safe-contour gate (CC-2a): refuse to translate any AI action whose target
  // command is not `aiCallable: true`. Fails closed before any EditorCommand
  // is produced. `descriptors` is injectable for tests / alternate registries.
  assertAiCallableAction(command.action, descriptors);
  switch (command.action) {
    case "CREATE_BLOCK":
      return translateCreateBlock(command);
    case "INSERT_BLOCK":
      return translateInsertBlock(command, context);
    case "EXPLODE":
      return translateExplode(command);
    case "UPDATE_ATTRIBUTE":
      return translateUpdateAttribute(command);
    case "CREATE_DYNAMIC_BLOCK":
      return translateCreateDynamicBlock(command);
    case "SET_VISIBILITY_STATE":
      return translateSetVisibilityState(command, context);
    case "LOOKUP_SELECT":
      return translateLookupSelect(command, context);
    case "UPDATE_DYNAMIC_PARAMETER":
      return translateUpdateDynamicParameter(command);
    case "SET_CANNOSCALE":
      return translateSetCannoscale(command);
    case "CREATE_ATTDEF":
      return translateCreateAttdef(command);
    case "UPDATE_ATTDEF":
      return translateUpdateAttdef(command);
    case "RENAME_ATTDEF_TAG":
      return translateRenameAttdefTag(command);
    case "REMOVE_ATTDEF":
      return translateRemoveAttdef(command);
    case "REORDER_ATTDEFS":
      return translateReorderAttdefs(command);
    case "PURGE_BLOCKS":
      return translatePurgeBlocks(command);
    case "RENAME_BLOCK":
      return translateRenameBlock(command);
  }
}

/**
 * Batch variant — flatten-translates a list of AI commands in order.
 */
export function translateAICommandBatch(
  commands: readonly BlockAiCommand[],
  context: AiDispatchContext = {},
  descriptors: readonly CommandDescriptor[] = ALL_COMMAND_DESCRIPTORS,
): EditorCommand[] {
  const all: EditorCommand[] = [];
  for (const command of commands) {
    all.push(...translateAICommandToDispatch(command, context, descriptors));
  }
  return all;
}
